import { cn } from '@/lib/utils';
import type { NavItem as NavItemType } from '@/types/navigation';
import { Link } from 'react-router-dom';

/**
 * Composant NavItem réutilisable
 * Gère la navigation et l'état actif
 */

interface NavItemProps {
  item: NavItemType;
  isActive?: boolean;
  onClick?: (itemId: string) => void;
}

export function NavItem({ item, isActive = false, onClick }: NavItemProps) {
  const { id, label, icon: Icon, href, badge, disabled } = item;

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(id);
    }
  };

  const content = (
    <>
      <div className="w-4 h-4 shrink-0" aria-hidden="true">
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm font-normal leading-none overflow-hidden text-ellipsis flex-1">
        {label}
      </span>
      {badge && (
        <span className="bg-zinc-200 text-zinc-700 text-xs px-1.5 py-0.5 rounded-full min-w-0 shrink-0">
          {badge}
        </span>
      )}
    </>
  );

  const className = cn(
    'box-border flex gap-2 h-8 items-center justify-start p-2 rounded-md w-full text-left transition-colors',
    isActive ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-700 hover:bg-zinc-50',
    disabled && 'opacity-50 cursor-not-allowed'
  );

  if (disabled) {
    return (
      <div className={className} aria-disabled="true" role="menuitem">
        {content}
      </div>
    );
  }

  return (
    <Link
      to={href}
      onClick={handleClick}
      className={className}
      role="menuitem"
      aria-current={isActive ? 'page' : undefined}
    >
      {content}
    </Link>
  );
}
